import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Dashboard from "@/pages/Dashboard";
import NewDiagnostic from "@/pages/NewDiagnostic";
import DiagnosticView from "@/pages/DiagnosticView";
import DiagnosticReport from "@/pages/DiagnosticReport";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Team from "@/pages/Team";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-16 h-16 border border-primary rounded-md scan-line" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function ShopOnly({ children }) {
  const { user, loading } = useAuth();
  const [checked, setChecked] = useState(null); // null=loading, true=allowed, false=redirect

  useEffect(() => {
    if (loading || !user) return;
    api.get("/credits")
      .then(({ data }) => setChecked(!!data.is_shop))
      .catch(() => setChecked(false));
  }, [loading, user]);

  if (loading || checked === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-16 h-16 border border-primary rounded-md scan-line" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!checked) return <Navigate to="/dashboard" replace />;
  return children;
}

// Guard: Shop settings page — requires an active subscription (any tier).
// Redirects no-plan shops to /billing so they can subscribe.
function SubscribedShopOnly({ children }) {
  const { user, loading } = useAuth();
  const [state, setState] = useState({ status: "loading" }); // loading | ok | not_shop | no_plan

  useEffect(() => {
    if (loading || !user) return;
    api.get("/credits")
      .then(({ data }) => {
        if (!data.is_shop) return setState({ status: "not_shop" });
        if (!data.plan) return setState({ status: "no_plan" });
        setState({ status: "ok" });
      })
      .catch(() => setState({ status: "not_shop" }));
  }, [loading, user]);

  useEffect(() => {
    if (state.status === "no_plan") {
      toast.error("Shop settings unlock with any subscription — pick a plan to continue.");
    }
  }, [state.status]);

  if (loading || state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-16 h-16 border border-primary rounded-md scan-line" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (state.status === "not_shop") return <Navigate to="/dashboard" replace />;
  if (state.status === "no_plan") return <Navigate to="/billing" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check for OAuth callback — MUST happen during render, not in useEffect
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/diagnostic/new" element={<Protected><NewDiagnostic /></Protected>} />
      <Route path="/diagnostic/:id" element={<Protected><DiagnosticView /></Protected>} />
      <Route path="/diagnostic/:id/report" element={<Protected><DiagnosticReport /></Protected>} />
      <Route path="/settings" element={<Protected><ShopOnly><Settings /></ShopOnly></Protected>} />
      <Route path="/billing" element={<Protected><ShopOnly><Billing /></ShopOnly></Protected>} />
      <Route path="/team" element={<Protected><ShopOnly><Team /></ShopOnly></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
